/** Shared match helpers for recommend people / vehicles modals. */

export function normLabel(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Exact or soft contains match between two labels. */
export function labelsMatch(a, b) {
  const na = normLabel(a);
  const nb = normLabel(b);
  if (!na || !nb) return false;
  return na === nb || na.includes(nb) || nb.includes(na);
}

export function lineNeedLabel(line) {
  if (!line) return '';
  return (
    line.catalogLabel ||
    line.equipmentType ||
    line.equipmentTypeName ||
    line.specification ||
    ''
  );
}

export function lineNeedTypeId(line) {
  if (!line) return null;
  return line.equipmentTypeId ?? null;
}

/**
 * @returns {'exact'|'partial'|null}
 * - exact: listed service/material/type matches the line need
 * - partial: offers that category but no listed item matches the need
 */
export function personMatchKind(person, line) {
  if (!person || !line) return null;
  const need = lineNeedLabel(line);
  const offerings = person.offerings || [];
  const lineType = line.lineType || 'equipment';

  if (lineType === 'special_service') {
    const services = person.specialServices || [];
    if (need && services.some((s) => labelsMatch(s, need))) return 'exact';
    if (offerings.includes('special_services')) return 'partial';
    return null;
  }

  if (lineType === 'material') {
    const materials = person.materials || [];
    const hits = materials.some((m) => {
      if (typeof m === 'string') return labelsMatch(m, need);
      return labelsMatch(m?.name, need) || labelsMatch(m?.category, need);
    });
    if (hits) return 'exact';
    if (offerings.includes('material_supply')) return 'partial';
    return null;
  }

  return null;
}

export function personIsMatch(person, line) {
  return personMatchKind(person, line) === 'exact';
}

export function assetMatchKind(asset, line) {
  if (!asset || !line) return null;
  const typeName = lineNeedLabel(line);
  const typeId = lineNeedTypeId(line);
  if (typeId != null && Number(asset.equipmentTypeId) === Number(typeId)) {
    return 'exact';
  }
  if (typeName && labelsMatch(asset.equipmentType, typeName)) return 'exact';
  // Soft: description / model mentions the requested type name.
  if (
    typeName &&
    (labelsMatch(asset.equipmentDescription, typeName) ||
      labelsMatch(asset.model, typeName) ||
      labelsMatch(asset.manufacturer, typeName))
  ) {
    return 'partial';
  }
  return null;
}

export function assetIsMatch(asset, line) {
  return assetMatchKind(asset, line) === 'exact';
}

/** Type label shown in the equipment recommend table (like Services for people). */
export function assetTypeLabel(asset) {
  return asset?.equipmentType || asset?.equipmentDescription || '';
}

export function personCapabilityLabels(person, line) {
  const lineType = line?.lineType || '';
  if (lineType === 'material') {
    return (person.materials || [])
      .map((m) => (typeof m === 'string' ? m : m?.name))
      .filter(Boolean);
  }
  const services = person.specialServices || [];
  if (services.length) return services;
  return (person.offerings || []).map((o) => {
    if (o === 'special_services') return 'Special services';
    if (o === 'material_supply') return 'Material supply';
    if (o === 'equipment') return 'Equipment';
    return o;
  });
}

export function matchBadgeProps(kind) {
  if (kind === 'exact') {
    return { status: 'verified', label: 'Match' };
  }
  if (kind === 'partial') {
    return { status: 'warning', label: 'Offers type' };
  }
  return { status: 'neutral', label: 'No match' };
}
